import { useMsal } from "@azure/msal-react";
import {
  SilentRequest,
  InteractionRequiredAuthError,
  type AccountInfo,
  type IPublicClientApplication
} from "@azure/msal-browser";
import { loginRequest } from "../msalConfig";

interface UseMsalAuthReturn {
  getToken: () => Promise<string>;
  login: () => Promise<void>;
  logout: () => void;
  account: AccountInfo | undefined;
  instance: IPublicClientApplication;
  isAuthenticated: boolean;
}

export const useMsalAuth = (): UseMsalAuthReturn => {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const isAuthenticated = accounts.length > 0;

  const getToken = async (): Promise<string> => {
    if (!account) {
      return "";
    }

    const silentRequest: SilentRequest = {
      scopes: loginRequest.scopes,
      account
    };

    try {
      const response = await instance.acquireTokenSilent(silentRequest);

      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        try {
          const response = await instance.acquireTokenPopup(silentRequest);

          return response.accessToken;
        } catch (popupError) {
          console.error("Interactive token acquisition failed:", popupError);

          return "";
        }
      }

      console.error("Token acquisition failed:", error);

      return "";
    }
  };

  const login = async (): Promise<void> => {
    if (isAuthenticated) {
      return;
    }

    try {
      await instance.loginRedirect({
        scopes: loginRequest.scopes
      });
    } catch (error) {
      // Silently handle interaction_in_progress errors
      if ((error as any)?.errorCode === "interaction_in_progress") {
        return;
      }

      console.error("Login error:", error);
    }
  };

  const logout = (): void => {
    if (account) {
      instance
        .logoutRedirect({
          account
        })
        .catch((error) => {
          // Silently handle interaction_in_progress errors
          if (error?.errorCode === "interaction_in_progress") {
            return;
          }

          console.error("Logout redirect error:", error);
        });
    } else {
      window.location.href = "/";
    }
  };

  return {
    getToken,
    login,
    logout,
    account,
    instance,
    isAuthenticated
  };
};
